// =============================================================================
// QuickEditPopover — Fast in-place editing overlay for piles and trackers
// =============================================================================

import { useEffect, useRef } from 'react'
import { X, Lock } from 'lucide-react'
import type { TrackerStatus } from '@/types/plant'
import { TRACKER_STATUS_META, PART_NO_CONFIG } from '@/types/plant'
import { getStepsForElementKey, type StepConfig } from '@/lib/layerRegistry'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface QuickEditState {
  type:            'pile' | 'tracker' | 'generic'
  x:               number
  y:               number
  codigoTracker:   string
  elementId?:      string          // ID único del elemento genérico
  codigoHinca?:    string
  posicion?:       number
  currentPartNo?:  string
  currentStatus?:  TrackerStatus   // pile overallStatus OR tracker status
  esMotora?:       boolean
  tieneDamper?:    boolean
  // generic fields
  elementKey?:     string          // e.g. 'zanjas_bt', 'inversores'
  featureId?:      string          // element ID for onChange
  featureLabel?:   string          // display label
  steps?:          readonly StepConfig[]
}

interface Props {
  edit:                    QuickEditState
  mapWidth:                number
  mapHeight:               number
  onClose:                 () => void
  onStatusChange:          (codigoTracker: string, status: TrackerStatus) => void
  onPileStatusChange:      (codigoHinca: string, status: TrackerStatus) => void
  onGenericStatusChange?:  (featureId: string, elementKey: string, stepCode: string) => void
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function QuickEditPopover({ edit, mapWidth, mapHeight, onClose, onStatusChange, onPileStatusChange, onGenericStatusChange }: Props) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose()
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [onClose])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  const POPUP_W = 240
  const genericSteps = edit.type === 'generic' ? (edit.steps ?? getStepsForElementKey(edit.elementKey ?? '')) : []
  const POPUP_H = edit.type === 'generic'
    ? Math.min(440, 116 + genericSteps.length * 34)
    : 268
  const rawX    = edit.x + 14
  const rawY    = edit.y - POPUP_H / 2
  const left    = Math.min(rawX,  mapWidth  - POPUP_W - 8)
  const top     = Math.max(8, Math.min(rawY, mapHeight - POPUP_H - 8))

  // Pile type config (read-only display)
  const partCfg = edit.currentPartNo
    ? PART_NO_CONFIG[edit.currentPartNo as keyof typeof PART_NO_CONFIG] ?? null
    : null

  const headerLabel = edit.type === 'pile'
    ? `H${String(edit.posicion ?? 0).padStart(2, '0')} · ${(edit.codigoHinca ?? '').split('-').slice(-1)[0] ?? ''}`
    : edit.type === 'tracker'
    ? edit.codigoTracker.split('-').slice(-3).join('-')
    : (edit.featureLabel ?? edit.featureId ?? edit.elementKey ?? '—')

  return (
    <div
      ref={ref}
      className="absolute z-30 pointer-events-auto"
      style={{ left, top, width: POPUP_W }}
    >
      <div
        className="bg-[#0F172A] border border-slate-600 rounded-xl shadow-2xl overflow-hidden"
        style={{ boxShadow: '0 0 40px #00000090, 0 0 0 1px #334155' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2.5 border-b border-slate-700 bg-[#1E293B]">
          <div className="flex items-center gap-2 min-w-0">
            <div className={`w-2 h-2 rounded-full shrink-0 ${edit.type === 'pile' ? 'bg-blue-400' : edit.type === 'tracker' ? 'bg-amber-400' : 'bg-emerald-400'}`} />
            <span className="text-[11px] font-mono text-slate-300 truncate">{headerLabel}</span>
          </div>
          <button onClick={onClose} className="p-0.5 text-slate-500 hover:text-white shrink-0 transition-colors">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-3 overflow-y-auto" style={edit.type === 'generic' ? { maxHeight: POPUP_H - 80 } : undefined}>

          {/* ── PILE editor ─────────────────────────────────────────── */}
          {edit.type === 'pile' && (
            <>
              {/* Read-only pile type info */}
              <div className="mb-3 px-2.5 py-2 bg-slate-800/50 rounded-lg border border-slate-700/50">
                <div className="flex items-center gap-1.5 mb-1">
                  <Lock className="w-2.5 h-2.5 text-slate-600" />
                  <p className="text-[9px] text-slate-500 uppercase tracking-wider">Tipo de hinca (referencia fija)</p>
                </div>
                {partCfg ? (
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full shrink-0 border border-white/20"
                      style={{ backgroundColor: partCfg.hex }}
                    />
                    <span className="text-slate-300 text-xs font-mono">{edit.currentPartNo}</span>
                    <span className="text-slate-500 text-[10px]">{partCfg.colorLabel}</span>
                    {partCfg.esMotora && (
                      <span className="ml-auto text-[9px] text-amber-400 font-bold">MOTOR</span>
                    )}
                  </div>
                ) : (
                  <span className="text-slate-600 text-xs">—</span>
                )}
                {edit.tieneDamper && (
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <div className="w-3 h-3 rounded-full border-2 border-yellow-400/70 bg-transparent shrink-0" />
                    <span className="text-[9px] text-yellow-400/80">Con DAMPER</span>
                  </div>
                )}
              </div>

              {/* Pile installation status selector */}
              <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">Estado de instalación</p>
              <div className="space-y-1">
                {(Object.entries(TRACKER_STATUS_META) as [TrackerStatus, typeof TRACKER_STATUS_META[TrackerStatus]][]).map(([status, meta]) => {
                  const isCurrent = edit.currentStatus === status
                  return (
                    <button
                      key={status}
                      onClick={() => { onPileStatusChange(edit.codigoHinca!, status); onClose() }}
                      className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs transition-all duration-150
                        ${isCurrent ? 'bg-slate-700/60' : 'hover:bg-slate-800/80'}`}
                      style={{ border: isCurrent ? `1px solid ${meta.color}50` : '1px solid transparent' }}
                    >
                      <div
                        className="w-3.5 h-3.5 rounded-full shrink-0"
                        style={{ backgroundColor: meta.color, boxShadow: isCurrent ? `0 0 8px ${meta.glowColor}` : undefined }}
                      />
                      <span className={`flex-1 text-left ${isCurrent ? 'text-white font-semibold' : 'text-slate-300'}`}>
                        {meta.label}
                      </span>
                      {isCurrent && <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />}
                    </button>
                  )
                })}
              </div>
            </>
          )}

          {/* ── TRACKER editor ──────────────────────────────────────── */}
          {edit.type === 'tracker' && (
            <>
              <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">Estado del tracker</p>
              <div className="space-y-1">
                {(Object.entries(TRACKER_STATUS_META) as [TrackerStatus, typeof TRACKER_STATUS_META[TrackerStatus]][]).map(([status, meta]) => {
                  const isCurrent = edit.currentStatus === status
                  return (
                    <button
                      key={status}
                      onClick={() => { onStatusChange(edit.codigoTracker, status); onClose() }}
                      className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs transition-all duration-150
                        ${isCurrent ? 'bg-slate-700/60' : 'hover:bg-slate-800/80'}`}
                      style={{ border: isCurrent ? `1px solid ${meta.color}50` : '1px solid transparent' }}
                    >
                      <div
                        className="w-3.5 h-3.5 rounded-full shrink-0"
                        style={{ backgroundColor: meta.color, boxShadow: isCurrent ? `0 0 8px ${meta.glowColor}` : undefined }}
                      />
                      <span className={`flex-1 text-left ${isCurrent ? 'text-white font-semibold' : 'text-slate-300'}`}>
                        {meta.label}
                      </span>
                      {isCurrent && <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />}
                    </button>
                  )
                })}
              </div>
            </>
          )}

          {/* ── GENERIC editor (WBS steps for any layer) ────────────── */}
          {edit.type === 'generic' && genericSteps.length > 0 && (
            <>
              <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">Seleccionar paso WBS</p>
              <div className="space-y-0.5 max-h-64 overflow-y-auto">
                {genericSteps.map((step: StepConfig) => (
                  <button
                    key={step.code}
                    onClick={() => {
                      onGenericStatusChange?.(edit.featureId ?? '', edit.elementKey ?? '', step.code)
                      onClose()
                    }}
                    className="w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-xs hover:bg-slate-800/80 transition-all duration-150"
                    style={{ border: '1px solid transparent' }}
                  >
                    <div
                      className="w-3 h-3 rounded-full shrink-0"
                      style={{ backgroundColor: step.color }}
                    />
                    <span className="text-[9px] opacity-50 w-8 shrink-0 font-mono">{step.code}</span>
                    <span className="flex-1 text-left text-slate-300 truncate">{step.label}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="px-3 pb-2.5">
          <p className="text-[9px] text-slate-700 text-center">
            {edit.type === 'generic' ? 'Selecciona un paso WBS · Esc para cerrar' : '2 clics para cambiar estado · Esc para cerrar'}
          </p>
        </div>
      </div>
    </div>
  )
}
